import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { RefreshCw, Check, X, Loader2, Package, Server, Clock, ChevronRight, ChevronDown, FileText, History } from 'lucide-react';
import { apiService, DeployRequestDTO } from '@/services/api.service';
import { useGameStore } from '@/stores/game.store';
import { useAuthStore } from '@/stores/auth.store';
import { usePendingRequestsStore } from '@/stores/pendingRequests.store';
import { followRelease } from '@/components/config/followRelease';
import { ImageDivergences } from '@/components/config/ImageDivergences';
import { statusLabel, whoLine } from './publicationsModel';
import styles from './DeployRequestsPage.module.scss';

/** Nombre de lignes d'historique affichées d'un coup (le serveur renvoie tout). */
const HISTORY_PAGE = 30;

/** Colore un diff git ligne par ligne (+, -, @@). */
function renderDiff(text: string): ReactNode[] {
  return text.split('\n').map((line, i) => {
    const cls = line.startsWith('+') ? styles.diffAdd : line.startsWith('-') ? styles.diffDel : line.startsWith('@@') ? styles.diffMeta : undefined;
    return <div key={i} className={cls}>{line || ' '}</div>;
  });
}

function timeAgo(ts: number): string {
  const s = Math.max(0, (Date.now() - ts) / 1000);
  if (s < 60) return "à l'instant";
  const m = Math.floor(s / 60);
  if (m < 60) return `il y a ${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `il y a ${h} h`;
  return `il y a ${Math.floor(h / 24)} j`;
}

const fmtDate = (ts: number) => new Date(ts).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });

const kindLabel = (r: { type: string }) => (r.type === 'container' ? 'Image' : 'Pack');
const KindIcon = ({ type }: { type: string }) => (type === 'container' ? <Server size={13} /> : <Package size={13} />);

/**
 * Publications : l'historique de ce que le panel a publié (qui, quand, quoi, résultat), les écarts
 * de réglages entre les images, et les demandes de ceux qui n'ont pas le droit de publier.
 * « Envoyer » publie directement pour qui a ce droit ; les autres envoient une demande, qu'un ayant
 * droit accepte (publication réelle) ou refuse (fichiers annulés) ici.
 */
export function DeployRequestsPage() {
  const project = useGameStore((s) => s.activeGameId);
  const decidedBy = useAuthStore((s) => s.user?.gamertag) ?? undefined;
  // Valider (et publier sans relecture) est un droit distinct d'éditer : sans lui, la page reste
  // consultable mais aucune décision n'est proposée. Le backend refuse de toute façon.
  const canDecide = useAuthStore((s) => s.canWrite)('config.requests');
  const [requests, setRequests] = useState<DeployRequestDTO[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [rowStatus, setRowStatus] = useState<Record<string, { ok: boolean; msg: string }>>({});
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [diffs, setDiffs] = useState<Record<string, string>>({});
  // L'historique n'est pas paginé côté serveur : on en affiche une tranche, extensible à la demande.
  const [historyLimit, setHistoryLimit] = useState(HISTORY_PAGE);
  const setPendingCount = usePendingRequestsStore((s) => s.setCount);
  const setProcessing = usePendingRequestsStore((s) => s.setProcessing);
  const fetchPending = usePendingRequestsStore((s) => s.fetch);

  const refresh = useCallback(async () => {
    setLoading(true);
    const r = await apiService.listDeployRequests(project);
    const list = r.data?.requests ?? [];
    setRequests(list);
    setPendingCount(list.filter((x) => x.status === 'pending').length);
    setSelected(new Set());
    setLoading(false);
  }, [project, setPendingCount]);

  useEffect(() => { refresh(); }, [refresh]);

  const pending = requests.filter((r) => r.status === 'pending');
  const selectedPending = pending.filter((r) => selected.has(r.id));

  /** Regroupe des demandes par cible (même container / même pack). */
  const groupByTarget = (list: DeployRequestDTO[]) => {
    const map = new Map<string, { key: string; type: string; targetId: string; targetName: string; requests: DeployRequestDTO[] }>();
    for (const r of list) {
      const key = `${r.type}::${r.targetId}`;
      let g = map.get(key);
      if (!g) { g = { key, type: r.type, targetId: r.targetId, targetName: r.targetName, requests: [] }; map.set(key, g); }
      g.requests.push(r);
    }
    return [...map.values()];
  };

  const grouped = useMemo(() => groupByTarget(pending), [requests]); // eslint-disable-line react-hooks/exhaustive-deps

  // Historique : tout ce qui a été tranché ou publié, le plus récent d'abord, dans l'ordre du temps.
  const history = useMemo(
    () => requests
      .filter((r) => r.status !== 'pending')
      .sort((a, b) => (b.decidedAt ?? b.createdAt) - (a.decidedAt ?? a.createdAt)),
    [requests]);

  const toggleTargetGroup = (reqs: DeployRequestDTO[]) => {
    const ids = reqs.filter((r) => r.status === 'pending').map((r) => r.id);
    setSelected((s) => {
      const n = new Set(s);
      const allSel = ids.length > 0 && ids.every((id) => n.has(id));
      ids.forEach((id) => (allSel ? n.delete(id) : n.add(id)));
      return n;
    });
  };

  const toggle = (id: string) =>
    setSelected((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  // Accepte une demande : publication réelle. Pour une image, suit la release (le serveur la finit seul).
  const acceptOne = async (req: DeployRequestDTO) => {
    setRowStatus((s) => ({ ...s, [req.id]: { ok: true, msg: 'Publication…' } }));
    const r = await apiService.acceptDeployRequest(req.id, decidedBy);
    const d = r.data;
    if (!d?.success) {
      setRowStatus((s) => ({ ...s, [req.id]: { ok: false, msg: d?.message ?? r.error ?? 'Échec' } }));
      return;
    }
    if (d.needsFinalize && d.containerId && d.version) {
      const f = await followRelease(d.containerId, d.version,
        (msg) => setRowStatus((s) => ({ ...s, [req.id]: { ok: true, msg } })));
      setRowStatus((s) => ({ ...s, [req.id]: f }));
      return;
    }
    setRowStatus((s) => ({ ...s, [req.id]: { ok: true, msg: d.message || 'Publié' } }));
  };

  const refuseOne = async (req: DeployRequestDTO) => {
    await apiService.refuseDeployRequest(req.id, decidedBy);
  };

  const toggleExpand = (id: string) =>
    setExpanded((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  const loadDiff = async (req: DeployRequestDTO, file: string) => {
    const key = `${req.id}::${file}`;
    if (diffs[key] !== undefined) return;
    setDiffs((d) => ({ ...d, [key]: '__loading__' }));
    const r = req.type === 'container'
      ? await apiService.getContainerDiff(req.targetId, file)
      : await apiService.getPackDiff(req.targetId, file);
    setDiffs((d) => ({ ...d, [key]: r.data?.diff || '(aucune différence / fichier illisible)' }));
  };

  // Enveloppe un traitement : indicateur global (spinner de nav, survit à la navigation) + refresh du compteur.
  const withProcessing = async (fn: () => Promise<void>) => {
    setBusy(true);
    setProcessing(true);
    try {
      await fn();
    } finally {
      setBusy(false);
      setProcessing(false);
      fetchPending(project); // met à jour le badge même si on a quitté la page
      await refresh();
    }
  };

  const doAccept = (req: DeployRequestDTO) => withProcessing(() => acceptOne(req));

  // Refuser ANNULE les fichiers sur le serveur : c'est destructif, on le dit avant.
  const doRefuse = (req: DeployRequestDTO) => {
    const n = req.files.length;
    if (!window.confirm(
      `Refuser cette demande annulera ${n} fichier${n > 1 ? 's' : ''} sur le serveur.\n\n`
      + `Les créations seront supprimées, les modifications revenues à leur état publié. `
      + `Les fichiers repris par une autre demande encore en attente sont conservés.`)) return;
    return withProcessing(() => refuseOne(req));
  };

  const runBulk = async (action: 'accept' | 'refuse') => {
    if (selectedPending.length === 0) return;
    if (action === 'refuse') {
      const files = selectedPending.reduce((n, r) => n + r.files.length, 0);
      if (!window.confirm(
        `Refuser ${selectedPending.length} demande(s) annulera ${files} fichier(s) sur le serveur.\n\n`
        + `Les créations seront supprimées, les modifications revenues à leur état publié.`)) return;
    }
    await withProcessing(async () => {
      // Parallèle par cible (repos distincts), séquentiel au sein d'une même cible. Pour le refus
      // c'est indispensable : en parallèle, chaque refus voyait les autres encore « en attente »
      // et leur laissait les fichiers partagés (jamais annulés), en plus des git concurrents sur
      // le même dépôt. En séquentiel, le dernier refus de la cible annule ce qui reste.
      const byTarget = new Map<string, DeployRequestDTO[]>();
      for (const r of selectedPending) {
        const k = `${r.type}::${r.targetId}`;
        const arr = byTarget.get(k) ?? [];
        arr.push(r);
        byTarget.set(k, arr);
      }
      await Promise.all([...byTarget.values()].map(async (reqs) => {
        for (const req of reqs) {
          if (action === 'refuse') await refuseOne(req);
          else await acceptOne(req);
        }
      }));
    });
  };

  /** Demande en attente : décision, détail des fichiers avec leur diff. */
  const renderPending = (req: DeployRequestDTO) => {
    const st = rowStatus[req.id];
    const isExp = expanded.has(req.id);
    return (
      <div key={req.id} className={styles.item}>
        <div className={styles.row}>
          {canDecide && (
            <input type="checkbox" disabled={busy} checked={selected.has(req.id)} onChange={() => toggle(req.id)} />
          )}
          <button className={styles.expandBtn} onClick={() => toggleExpand(req.id)} title="Voir le détail">
            {isExp ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </button>
          <div className={styles.info}>
            <div className={styles.target}>
              {req.files.length} fichier{req.files.length > 1 ? 's' : ''}
              {req.message ? <span className={styles.fileCount}> · « {req.message} »</span> : null}
            </div>
            <div className={styles.meta}>
              {whoLine(req)} · <Clock size={11} /> {timeAgo(req.createdAt)}
            </div>
            {st && <div className={st.ok ? styles.rowOk : styles.rowErr}>{st.msg}</div>}
          </div>
          <span className={`${styles.statusChip} ${styles.chipPending}`}>En attente</span>
          {canDecide && (
            <div className={styles.rowActions}>
              <button className={styles.acceptBtn} disabled={busy} onClick={() => doAccept(req)}><Check size={14} /> Accepter</button>
              <button className={styles.refuseBtn} disabled={busy} onClick={() => doRefuse(req)}><X size={14} /> Refuser</button>
            </div>
          )}
        </div>

        {isExp && (
          <div className={styles.detail}>
            {req.files.length === 0 ? (
              <div className={styles.muted}>Aucun fichier.</div>
            ) : req.files.map((f) => {
              const key = `${req.id}::${f}`;
              const diff = diffs[key];
              return (
                <div key={f} className={styles.fileBlock}>
                  <button className={styles.fileName} onClick={() => loadDiff(req, f)}>
                    <FileText size={13} /> {f}
                  </button>
                  {diff === '__loading__' ? (
                    <div className={styles.muted}>Chargement du diff…</div>
                  ) : diff !== undefined ? (
                    <pre className={styles.diffPre}>{renderDiff(diff)}</pre>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  /** Ligne d'historique : quoi (cible, fichiers, message), qui, quand, et le résultat (version de l'image...). */
  const renderHistory = (req: DeployRequestDTO) => {
    const st = rowStatus[req.id];
    const isExp = expanded.has(req.id);
    const at = req.decidedAt ?? req.createdAt;
    const label = statusLabel(req);
    const chip = {
      cls: label.tone === 'ok' ? styles.chipAccepted : label.tone === 'pending' ? styles.chipPending : styles.chipRefused,
      txt: label.text,
    };
    return (
      <div key={req.id} className={`${styles.item} ${req.status === 'refused' || req.status === 'failed' ? styles.rowRefused : ''}`}>
        <div className={styles.row}>
          <button className={styles.expandBtn} onClick={() => toggleExpand(req.id)} title="Voir les fichiers">
            {isExp ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </button>
          <span className={styles.typeBadge}><KindIcon type={req.type} /> {kindLabel(req)}</span>
          <div className={styles.info}>
            <div className={styles.target}>
              {req.targetName}
              <span className={styles.fileCount}>
                {' '}· {req.files.length} fichier{req.files.length > 1 ? 's' : ''}
                {req.message ? <> · « {req.message} »</> : null}
              </span>
            </div>
            <div className={styles.meta}>
              {whoLine(req)} · <Clock size={11} /> {fmtDate(at)} ({timeAgo(at)})
            </div>
            {st ? <div className={st.ok ? styles.rowOk : styles.rowErr}>{st.msg}</div>
              : req.result ? <div className={styles.muted}>{req.result}</div> : null}
          </div>
          <span className={`${styles.statusChip} ${chip.cls}`}>{chip.txt}</span>
        </div>
        {isExp && (
          <div className={styles.detail}>
            {req.files.length === 0 ? <div className={styles.muted}>Aucun fichier.</div>
              : req.files.map((f) => <div key={f} className={styles.filePlain}><FileText size={13} /> {f}</div>)}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>Publications</h1>
          <p className={styles.subtitle}>
            Ce que le panel a publié : qui, quand, quoi. « Envoyer » publie directement pour qui en a le droit ;
            les demandes des autres attendent ici une décision. Une image publiée depuis le panel ne change que ses
            réglages, jamais la version d'un mod.
          </p>
        </div>
        <button className={styles.iconBtn} onClick={refresh} disabled={loading || busy} title="Rafraîchir">
          <RefreshCw size={16} className={loading ? styles.spin : undefined} />
        </button>
      </header>

      <ImageDivergences canAck={canDecide} />

      {pending.length > 0 && (
        <section className={styles.pendingSection}>
          <h2 className={styles.sectionTitle}>
            Demandes en attente <span className={styles.groupCount}>({pending.length})</span>
          </h2>

          {!canDecide && (
            <p className={styles.muted}>
              Une personne qui a le droit de publier (« Publications » en écriture) les acceptera ou les refusera.
            </p>
          )}

          {canDecide && (
            <div className={styles.bulkBar}>
              <label className={styles.selAll}>
                <input
                  type="checkbox"
                  checked={selectedPending.length > 0 && selectedPending.length === pending.length}
                  onChange={(e) => setSelected(e.target.checked ? new Set(pending.map((r) => r.id)) : new Set())}
                />
                Tout sélectionner ({pending.length} en attente)
              </label>
              <span className={styles.spacer} />
              <button className={styles.acceptBtn} disabled={busy || selectedPending.length === 0} onClick={() => runBulk('accept')}>
                {busy ? <Loader2 size={14} className={styles.spin} /> : <Check size={14} />} Accepter ({selectedPending.length})
              </button>
              <button className={styles.refuseBtn} disabled={busy || selectedPending.length === 0} onClick={() => runBulk('refuse')}>
                <X size={14} /> Refuser ({selectedPending.length})
              </button>
            </div>
          )}

          <div className={styles.list}>
            {grouped.map((g) => {
              const allGroupSel = g.requests.length > 0 && g.requests.every((r) => selected.has(r.id));
              return (
                <div key={g.key} className={styles.targetGroup}>
                  <div className={styles.targetHead}>
                    {canDecide && (
                      <input type="checkbox" checked={allGroupSel} disabled={busy} onChange={() => toggleTargetGroup(g.requests)} />
                    )}
                    <span className={styles.typeBadge}><KindIcon type={g.type} /> {kindLabel(g)}</span>
                    <span className={styles.targetName}>{g.targetName}</span>
                    <span className={styles.groupCount}>{g.requests.length} en attente</span>
                  </div>
                  <div className={styles.targetBody}>
                    {g.requests.map(renderPending)}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      <section className={styles.history}>
        <h2 className={styles.sectionTitle}>
          <History size={14} /> Historique
          {history.length > 0 && <span className={styles.groupCount}> ({history.length})</span>}
        </h2>
        {loading && history.length === 0 ? (
          <div className={styles.center}><Loader2 size={22} className={styles.spin} /> Chargement…</div>
        ) : history.length === 0 ? (
          <div className={styles.center}>Rien n'a encore été publié depuis le panel pour ce projet.</div>
        ) : (
          <div className={styles.historyList}>
            {history.slice(0, historyLimit).map(renderHistory)}
            {history.length > historyLimit && (
              <button type="button" className={styles.moreBtn} onClick={() => setHistoryLimit((n) => n + HISTORY_PAGE)}>
                Afficher {Math.min(HISTORY_PAGE, history.length - historyLimit)} de plus
                <span className={styles.groupCount}> ({history.length - historyLimit} restantes)</span>
              </button>
            )}
          </div>
        )}
      </section>
    </div>
  );
}
